import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ==================================================================
   The Capping Rite — a scroll-scrubbed living comb.
   Scroll pours "liquid" honey into a hand-drawn comb: the surface lags
   with viscous damping, tilts against scroll acceleration, wobbles on
   stop, and drains slower than it filled (leaving a wet film). The
   bees' quality ritual — build, ripen, cap, harvest — is the story.
   All state derives from scroll position; nothing hijacks scrolling.
   ================================================================== */

const VB_W = 1000;
const VB_H = 900;
const R = 52;               // hex radius (center to corner), flat-top
const HW = R * 0.8660254;   // half-height
const CX = 500;
const CY = 430;

function mulberry32(seed) {
  let a = seed;
  return function rand() {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* Deterministic comb geometry, computed once at module load. */
const GEOMETRY = (() => {
  const rand = mulberry32(2003);
  const cells = [];
  for (let q = -4; q <= 4; q += 1) {
    for (let r = -3; r <= 3; r += 1) {
      const x = CX + q * 1.5 * R;
      const y = CY + r * 2 * HW + (Math.abs(q) % 2 === 1 ? HW : 0);
      const dist = Math.hypot(x - CX, y - CY);
      // ragged wild-comb silhouette: outer cells drop out
      if (dist > 300 && rand() < 0.55) continue;
      if (dist > 360) continue;
      cells.push({
        x: Math.round(x + (rand() - 0.5) * 5),
        y: Math.round(y + (rand() - 0.5) * 5),
        dist,
        angle: Math.atan2(y - CY, x - CX),
      });
    }
  }
  // spiral order from the founding (center) cell outward
  cells.sort((a, b) => (a.dist - b.dist) || (a.angle - b.angle));
  cells.forEach((cell, index) => { cell.spiral = index; });

  // hero cell: bottom-center — its cap becomes the harvest lid
  let hero = cells[0];
  for (const cell of cells) {
    if (Math.hypot(cell.x - CX, cell.y - 660) < Math.hypot(hero.x - CX, hero.y - 660)) hero = cell;
  }

  // cap set: everything except the bottom fringe; deterministic shuffle
  const caps = cells.filter((cell) => cell !== hero && cell.y < 700);
  for (let i = caps.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [caps[i], caps[j]] = [caps[j], caps[i]];
  }
  caps.forEach((cell, index) => { cell.capOrder = index; });

  // letterpress words + gold seals on a few mid-comb caps
  const inner = caps.filter((cell) => cell.dist < 210);
  const words = ['RAW', 'UNFILTERED', 'SINGLE-ORIGIN'];
  const wordCells = inner.slice(0, 3);
  const sealCells = inner.slice(3, 6);

  // wet-film rows: distinct row bands
  const rowYs = [...new Set(cells.map((cell) => Math.round(cell.y / (2 * HW)) * 2 * HW))].sort((a, b) => a - b);

  return { cells, hero, caps, wordCells, words, sealCells, rowYs };
})();

const HEX_POINTS = (radius) => {
  const h = radius * 0.8660254;
  return `${radius},0 ${radius / 2},${h} ${-radius / 2},${h} ${-radius},0 ${-radius / 2},${-h} ${radius / 2},${-h}`;
};

// liquid geometry: wave line rests at y=150 when --level is 0 (crest)
const LIQUID_TRAVEL = 720; // px of translateY between crest and drained
const SURFACE_TOP = 150;

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smooth = (p, a, b) => {
  const t = clamp01((p - a) / (b - a));
  return t * t * (3 - 2 * t);
};

export default function CombRite({ ctaHref = '/' }) {
  const sectionRef = useRef(null);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) return undefined; // CSS poster state stands

    const section = sectionRef.current;
    if (!section) return undefined;
    const stage = section.querySelector('.comb-rite__stage');
    const filmRows = [...section.querySelectorAll('.cr-film-row')];

    let sectionTop = 0;
    let runway = 1;
    let narrow = false;
    function measure() {
      let top = 0;
      let node = section;
      while (node) { top += node.offsetTop; node = node.offsetParent; }
      sectionTop = top;
      runway = Math.max(1, section.offsetHeight - window.innerHeight);
      narrow = window.innerWidth < 860;
    }
    measure();

    let raf = 0;
    let running = false;
    let dirty = true;
    let lastTime = 0;
    let level = 1;      // 1 = drained (below comb), 0 = crest
    let tilt = 0;
    let tiltVel = 0;
    let velocity = 0;
    let prevRaw = -1;
    let highWater = 2;  // min level ever reached (lower = higher tide)
    let lite = false;
    let slowFrames = 0;

    function frame(time) {
      raf = running ? requestAnimationFrame(frame) : 0;
      const dt = Math.min(0.032, lastTime ? (time - lastTime) / 1000 : 0.016);
      if (lastTime && dt >= 0.024 && (slowFrames += 1) > 5 && !lite) {
        lite = true;
        section.classList.add('is-lite');
      }
      lastTime = time;

      const raw = clamp01((window.scrollY - sectionTop) / runway);
      const scrollDelta = prevRaw < 0 ? 0 : raw - prevRaw;
      prevRaw = raw;

      // velocity EMA with deadzone (idle trackpads must not jitter the tilt)
      velocity += (scrollDelta - velocity) * 0.15;
      const v = Math.abs(velocity) < 0.0002 ? 0 : velocity;

      const p2 = smooth(raw, 0.24, 0.6);
      const target = 1 - p2;

      // asymmetric viscosity: fills faster than it drains
      level += (target - level) * (target < level ? 0.14 : 0.055);

      // underdamped tilt spring driven by scroll acceleration
      if (!lite) {
        tiltVel += (-140 * tilt - 7 * tiltVel - v * 900) * dt;
        tilt += tiltVel * dt;
        const maxTilt = narrow ? 3 : 6;
        tilt = Math.max(-maxTilt, Math.min(maxTilt, tilt));
      } else {
        tilt = 0; tiltVel = 0; level = target;
      }

      // drain drips appear when the target runs away below the level
      const dripT = clamp01((level - target - 0.02) * 8);

      // wet-film high-water memory
      if (level < highWater) {
        highWater = level;
        const surfaceY = SURFACE_TOP + highWater * LIQUID_TRAVEL;
        for (const row of filmRows) {
          if (Number(row.dataset.y) > surfaceY) row.classList.add('is-wet');
        }
      }

      if (dirty || Math.abs(level - target) > 0.0008 || Math.abs(tilt) > 0.02 || Math.abs(tiltVel) > 0.02) {
        dirty = false;
        const style = section.style;
        style.setProperty('--p', raw.toFixed(4));
        style.setProperty('--p1', smooth(raw, 0.05, 0.28).toFixed(4));
        style.setProperty('--p2', p2.toFixed(4));
        style.setProperty('--p3', smooth(raw, 0.55, 0.8).toFixed(4));
        style.setProperty('--p4', smooth(raw, 0.78, 1).toFixed(4));
        style.setProperty('--level', level.toFixed(4));
        style.setProperty('--tilt', `${tilt.toFixed(3)}deg`);
        style.setProperty('--dripT', dripT.toFixed(3));
      }
    }

    function onScroll() {
      dirty = true;
      // safety: if the loop is parked, write one frame so the scene never
      // freezes out of sync with the scroll position
      if (!running && !raf) {
        raf = requestAnimationFrame((time) => { raf = 0; frame(time); });
      }
    }
    function onResize() {
      measure();
      dirty = true;
    }

    const observer = new IntersectionObserver((entries) => {
      // entries batch — only the newest one reflects current visibility
      const visible = entries[entries.length - 1].isIntersecting;
      if (visible && !running) {
        running = true;
        lastTime = 0;
        prevRaw = -1;
        // snap on (re)entry so deep links don't play a phantom slosh
        const raw = clamp01((window.scrollY - sectionTop) / runway);
        level = 1 - smooth(raw, 0.24, 0.6);
        tilt = 0; tiltVel = 0; velocity = 0; dirty = true;
        raf = requestAnimationFrame(frame);
      } else if (!visible && running) {
        running = false;
        if (raf) cancelAnimationFrame(raf);
        if (window.scrollY + window.innerHeight < sectionTop || window.scrollY > sectionTop + section.offsetHeight) {
          highWater = 2; // fully left: the next visit replays fresh
          for (const row of filmRows) row.classList.remove('is-wet');
        }
      }
    }, { rootMargin: '100% 0px' });

    observer.observe(section);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    frame(0);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
      running = false;
    };
  }, []);

  const { cells, hero, caps, wordCells, words, sealCells, rowYs } = GEOMETRY;

  return (
    <section className="comb-rite" ref={sectionRef} aria-label="The Capping Rite — how our honey is made">
      <div className="comb-rite__stage">
        <div className="comb-rite__copy">
          <p className="cr-kicker">Our craft · since 2003</p>

          <figure className="cr-phase" style={{ '--cin': 0.05, '--cout': 0.27 }}>
            <span className="cr-step">01 — Build</span>
            <h2>Architecture no human improved on.</h2>
            <p>
              Every La Bella jar begins as empty wax — six perfect walls, drawn in
              darkness by ten thousand builders. We&rsquo;ve kept their blueprint since 2003.
            </p>
          </figure>

          <figure className="cr-phase" style={{ '--cin': 0.28, '--cout': 0.55 }}>
            <span className="cr-step">02 — Ripen</span>
            <h2>Nectar becomes honey slowly, or not at all.</h2>
            <p>
              Wild Mediterranean nectar thickens in the dark for weeks — fanned by
              wings, never by machines. Moisture falls, sugars deepen. We do not hurry it.
            </p>
          </figure>

          <figure className="cr-phase" style={{ '--cin': 0.56, '--cout': 0.78 }}>
            <span className="cr-step">03 — Cap</span>
            <h2>Bees seal a cell only when the honey is ready.</h2>
            <p>
              A wax cap is the hive&rsquo;s signature of ripeness. We harvest capped comb
              only — nature&rsquo;s own quality control, and ours.
            </p>
          </figure>

          <figure className="cr-phase cr-phase--last" style={{ '--cin': 0.84, '--cout': 2 }}>
            <span className="cr-step">04 — Harvest</span>
            <h2>Harvested at ripeness. Never rushed.</h2>
            <p>Raw Mediterranean honey &amp; hive treasures — cell by cell since 2003.</p>
            <span className="cr-rule" aria-hidden="true" />
            <Link className="cr-cta" to={ctaHref}>Shop the Harvest</Link>
          </figure>
        </div>

        <svg
          className="comb-rite__scene"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <polygon id="crHex" points={HEX_POINTS(R)} pathLength="1" />
            <polygon id="crHexIn" points={HEX_POINTS(R - 3)} />
            <polygon id="crHexCap" points={HEX_POINTS(R - 5)} />
            <linearGradient id="crGold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#d3b158" />
              <stop offset="0.52" stopColor="#a8862a" />
              <stop offset="1" stopColor="#83671a" />
            </linearGradient>
            <radialGradient id="crGlowGrad" cx="0.5" cy="0.55" r="0.55">
              <stop offset="0" stopColor="#cfa94a" stopOpacity="0.5" />
              <stop offset="1" stopColor="#cfa94a" stopOpacity="0" />
            </radialGradient>
            <mask id="crCells">
              <rect x="0" y="0" width={VB_W} height={VB_H} fill="#000" />
              {cells.map((cell, index) => (
                <use key={index} href="#crHexIn" x={cell.x} y={cell.y} fill="#fff" />
              ))}
            </mask>
          </defs>

          {/* candling backlight */}
          <rect className="cr-glow" x="60" y="30" width="880" height="840" fill="url(#crGlowGrad)" />

          <g className="cr-world">
            {/* wet film rows (drain memory) */}
            <g mask="url(#crCells)">
              {rowYs.map((y) => (
                <rect
                  key={y}
                  className="cr-film-row"
                  data-y={y}
                  x="0"
                  y={y - HW}
                  width={VB_W}
                  height={2 * HW}
                  fill="#a8862a"
                />
              ))}
            </g>

            {/* the liquid, visible only through the cells */}
            <g mask="url(#crCells)">
              <g className="cr-honey">
                <path
                  className="cr-honey__slab"
                  fill="url(#crGold)"
                  d={`M -260 ${SURFACE_TOP + 26}
                      C 0 ${SURFACE_TOP - 2}, 240 ${SURFACE_TOP + 40}, 500 ${SURFACE_TOP + 18}
                      C 760 ${SURFACE_TOP - 4}, 1010 ${SURFACE_TOP + 38}, 1260 ${SURFACE_TOP + 20}
                      L 1260 ${SURFACE_TOP + 1500} L -260 ${SURFACE_TOP + 1500} Z`}
                />
                <path
                  className="cr-honey__shine"
                  fill="none"
                  stroke="#e8cd7c"
                  strokeWidth="3"
                  d={`M -260 ${SURFACE_TOP + 26}
                      C 0 ${SURFACE_TOP - 2}, 240 ${SURFACE_TOP + 40}, 500 ${SURFACE_TOP + 18}
                      C 760 ${SURFACE_TOP - 4}, 1010 ${SURFACE_TOP + 38}, 1260 ${SURFACE_TOP + 20}`}
                />
              </g>
              {/* drain drips clinging to walls */}
              <g className="cr-drips">
                {[300, 520, 690].map((x, index) => (
                  <rect key={index} className="cr-drip" x={x} y={SURFACE_TOP + 40} width="4" height="70" rx="2" fill="#b58f2e" style={{ '--d': index }} />
                ))}
              </g>
            </g>

            {/* the lattice draws itself in a spiral */}
            <g className="cr-lattice">
              {cells.map((cell, index) => (
                <use
                  key={index}
                  href="#crHex"
                  x={cell.x}
                  y={cell.y}
                  className="cr-wall"
                  style={{ '--i': cell.spiral }}
                />
              ))}
            </g>

            {/* wax caps, sealed in ripening order (outer g holds the position —
                CSS transforms on the inner g would otherwise clobber it) */}
            <g className="cr-caps">
              {caps.map((cell, index) => (
                <g key={index} transform={`translate(${cell.x} ${cell.y})`}>
                  <g className="cr-cap" style={{ '--i': cell.capOrder }}>
                    <polygon points={HEX_POINTS(R - 5)} fill="#e9dcba" opacity="0.92" />
                    <polygon points={HEX_POINTS(R - 11)} fill="none" stroke="#c9b382" strokeWidth="1.5" opacity="0.7" />
                    {sealCells.includes(cell) ? (
                      <polygon points={HEX_POINTS(9)} fill="url(#crGold)" />
                    ) : null}
                    {wordCells.includes(cell) ? (
                      <text className="cr-word" y="4">{words[wordCells.indexOf(cell)]}</text>
                    ) : null}
                  </g>
                </g>
              ))}
            </g>

            {/* harvest: the hero lid opens and one drop falls */}
            <g className="cr-harvest" transform={`translate(${hero.x} ${hero.y})`}>
              <g className="cr-lid">
                <polygon points={HEX_POINTS(R - 5)} fill="#e9dcba" opacity="0.95" />
                <polygon points={HEX_POINTS(R - 11)} fill="none" stroke="#c9b382" strokeWidth="1.5" opacity="0.7" />
              </g>
              <rect className="cr-thread" x="-2.5" y={HW - 8} width="5" height="80" rx="2.5" fill="url(#crGold)" />
              <circle className="cr-drop" cx="0" cy={HW + 76} r="11" fill="url(#crGold)" />
            </g>
          </g>
        </svg>

        <div className="cr-scroll-hint" aria-hidden="true">
          <span className="cr-scroll-hint__line" />
          Scroll
        </div>
      </div>
    </section>
  );
}
